import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConstraintListElement,
  CopyConstraintParams,
  DeleteConstraintParams,
  NewConstraintParams,
  Optimization,
  RunOptimizationParams,
  StageTwoJobsInitiated
} from './linear-optimizations-constants';
import {
  LinearBuyingLambdaFetch,
  LinearOptimizationsLambdaFetch,
  awaitJSON,
  getUserFacingMessage
} from '../../utils/fetch-utils';
import { useSetError } from '../../redux/modals';

const jsonHeaders = { Accept: 'application/json', 'Content-Type': 'application/json' };

const optimizationsQueryKey = (company: string) => ['linearOptimizations', company];
const constraintsListQueryKey = (company: string) => ['linearConstraintsList', company];

// Polls while any run's status isn't yet a terminal SUCC*/FAIL* -- mirrors the legacy
// listUntilJobsFinish loop, but as a native React Query refetchInterval instead of a hand-rolled
// setTimeout chain.
export const useOptimizations = (company: string) =>
  useQuery({
    queryKey: optimizationsQueryKey(company),
    queryFn: async () => {
      const result = await LinearOptimizationsLambdaFetch('/optimizations', {
        params: { company }
      });
      return awaitJSON<Optimization[]>(result);
    },
    refetchInterval: query => {
      const { data } = query.state;
      if (!data) return false;
      const stillRunning = data.filter(
        row => !row.status.includes('SUCC') && !row.status.includes('FAIL')
      ).length;
      return stillRunning > 0 ? 30_000 : false;
    }
  });

export const useConstraintsList = (company: string) =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: constraintsListQueryKey(company),
    queryFn: async () => {
      const result = await LinearOptimizationsLambdaFetch('/list_constraints', {
        params: { company }
      });
      return awaitJSON<ConstraintListElement[]>(result);
    }
  });

export const useOptimizationKpis = (company: string) =>
  useQuery({
    queryKey: ['linearOptimizationKpis', company],
    queryFn: async () => {
      const result = await LinearOptimizationsLambdaFetch('/optimization_kpis', {
        params: { company }
      });
      return awaitJSON<string[]>(result);
    },
    // OptimizationHome.tsx already surfaces this query's error via useSetError — suppress the
    // global QueryCache fallback (src/main.tsx) so it doesn't double-show the dialog.
    meta: { suppressGlobalError: true }
  });

export const useValidWeeks = (company: string) =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['linearValidWeeks', company],
    queryFn: async () => {
      const result = await LinearBuyingLambdaFetch('/get_plan_log_weeks', { params: { company } });
      return awaitJSON<string[]>(result);
    }
  });

export const useStageTwoJobsInitiated = (company: string) =>
  useQuery({
    meta: { suppressGlobalError: true },
    queryKey: ['linearStageTwoJobsInitiated', company],
    queryFn: async () => {
      const result = await LinearOptimizationsLambdaFetch('/get_stage_two_jobs_initiated', {
        params: { company }
      });
      return awaitJSON<StageTwoJobsInitiated>(result);
    }
  });

export const useCopyConstraint = (company: string) => {
  const queryClient = useQueryClient();
  const setError = useSetError();
  return useMutation({
    mutationFn: async ({ from, to, optimizationType, week }: CopyConstraintParams) => {
      const existing = await awaitJSON<Record<string, unknown>>(
        await LinearOptimizationsLambdaFetch('/constraints', { params: { company, name: from } })
      );
      const body: Record<string, unknown> = { ...existing, name: to };
      if (optimizationType && week) {
        body.optimizationType = optimizationType;
        body.week = week;
      }
      const constraints = (body.constraints ?? {}) as { audience?: unknown[] };
      constraints.audience ??= [];
      body.constraints = constraints;
      await LinearOptimizationsLambdaFetch('/constraints', {
        method: 'POST',
        headers: jsonHeaders,
        body
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: constraintsListQueryKey(company) }),
    onError: error =>
      setError({
        title: "Couldn't copy constraint",
        message: getUserFacingMessage(error, "Couldn't copy constraint"),
        reportError: error
      })
  });
};

export const useNewConstraint = (company: string) => {
  const queryClient = useQueryClient();
  const setError = useSetError();
  return useMutation({
    mutationFn: async ({ to, optimizationType, week }: NewConstraintParams) => {
      const body: Record<string, unknown> = {
        company,
        name: to,
        constraints: {
          spot: [],
          avoid: [],
          total: 100_000,
          budget: [],
          audience: [],
          global: {
            totalSpots: 2000,
            minExtrapolation: 6,
            unseenMaxSpendPct: 0.25,
            networkMaxSpendPct: 0.2,
            single15sSpotMaxCost: 1800,
            single30sSpotMaxCost: 3000,
            networkDayDaypartCountPerHour: 1.5,
            networkDayDaypartMaxSpendPerHour: 2500,
            networkDayDaypartReachTypeCountPerHour: 1.5
          },
          version: 4,
          creativesAllowed: [],
          networkCreativesDisallowed: []
        }
      };
      if (optimizationType && week) {
        body.optimizationType = optimizationType;
        body.week = week;
        (body.constraints as Record<string, unknown>).week = week;
      }
      await LinearOptimizationsLambdaFetch('/constraints', {
        method: 'POST',
        headers: jsonHeaders,
        body
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: constraintsListQueryKey(company) }),
    onError: error =>
      setError({
        title: "Couldn't create constraint",
        message: getUserFacingMessage(error, "Couldn't create constraint"),
        reportError: error
      })
  });
};

export const useDeleteConstraint = (company: string) => {
  const queryClient = useQueryClient();
  const setError = useSetError();
  return useMutation({
    mutationFn: async ({ deleteConstraintId }: DeleteConstraintParams) => {
      await LinearOptimizationsLambdaFetch('/delete_constraints', {
        method: 'POST',
        headers: jsonHeaders,
        body: { company, name: deleteConstraintId }
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: constraintsListQueryKey(company) }),
    onError: error =>
      setError({
        title: "Couldn't delete constraint",
        message: getUserFacingMessage(error, "Couldn't delete constraint"),
        reportError: error
      })
  });
};

export const useRunOptimization = (company: string) => {
  const queryClient = useQueryClient();
  const setError = useSetError();
  return useMutation({
    mutationFn: async ({ name, kpi, branch, optimizationType, week }: RunOptimizationParams) => {
      await LinearOptimizationsLambdaFetch('/run_batch_optimizer', {
        method: 'POST',
        headers: jsonHeaders,
        body: { name, branch, kpi, optimizationType, week }
      });
    },
    onSuccess: () => {
      // The new run doesn't appear in /optimizations immediately -- matches the legacy 5s delay
      // before the old hand-rolled poll loop's first refresh.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: optimizationsQueryKey(company) });
      }, 5000);
    },
    onError: error =>
      setError({
        title: "Couldn't run optimization",
        message: getUserFacingMessage(error, "Couldn't run optimization"),
        reportError: error
      })
  });
};
